import { motion, useReducedMotion } from "framer-motion";
import { Check, CalendarPlus, Download, PartyPopper } from "lucide-react";
import { eventConfig } from "@/config/eventConfig";
import { analytics } from "@/lib/analytics";
import { googleCalendarUrl, downloadICS, whatsappShareUrl } from "@/lib/calendar";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Post-registration confirmation card with calendar + share actions.
export const SuccessCard = ({ saved }) => {
  const reduce = useReducedMotion();
  const { registration, event } = eventConfig;
  const firstName = (saved?.full_name || "").split(" ")[0];

  return (
    <motion.div
      data-testid="registration-success"
      initial={reduce ? false : { opacity: 0, scale: 0.98 }}
      animate={reduce ? false : { opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(31,145,136,0.12)", color: "#126F6A" }}>
          <PartyPopper size={24} />
        </span>
        <div>
          <h3 className="text-xl font-bold text-[#122131]">
            {registration.successHeading}, {firstName}.
          </h3>
        </div>
      </div>
      <p className="mt-4 text-[#647384] leading-relaxed">{registration.successSupporting}</p>

      <div className="mt-5 rounded-2xl bg-[#FBF8F0] border border-[rgba(17,42,67,0.10)] p-4">
        <div className="text-sm font-semibold text-[#122131]">{event.dateLabel} · {event.timeLabel}</div>
        <div className="text-sm text-[#647384] mt-1">{event.venue || event.venueSuccessPlaceholder}</div>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <a
          href={googleCalendarUrl(event)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="add-to-calendar-button"
          onClick={() => analytics.calendarAdd({ type: "google" })}
          className="inline-flex items-center justify-center gap-2 h-12 px-4 rounded-xl bg-[#1F9188] text-white font-semibold hover:bg-[#126F6A] transition-colors"
        >
          <CalendarPlus size={18} /> Add to Google Calendar
        </a>
        <button
          data-testid="download-ics-button"
          onClick={() => { downloadICS(event); analytics.calendarAdd({ type: "ics" }); }}
          className="inline-flex items-center justify-center gap-2 h-12 px-4 rounded-xl border border-[rgba(17,42,67,0.15)] text-[#112A43] font-semibold hover:bg-[rgba(17,42,67,0.04)] transition-colors"
        >
          <Download size={18} /> Download .ics
        </button>
      </div>
      <a
        href={whatsappShareUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-share-button"
        className="mt-3 inline-flex items-center justify-center gap-2 h-12 w-full rounded-xl font-semibold text-white"
        style={{ background: "#25D366" }}
      >
        <WhatsAppIcon /> Share on WhatsApp
      </a>

      <div className="mt-6">
        <div className="text-sm font-semibold text-[#122131] mb-2">What happens next?</div>
        <ul className="space-y-2">
          {registration.whatNext.map((w) => (
            <li key={w} className="flex items-start gap-2 text-sm text-[#647384]">
              <Check size={16} className="mt-0.5" style={{ color: "#126F6A" }} /> {w}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};
